export class TreeNode<ID extends number | string, T extends { id: ID }> {
  data: T;
  children: Map<ID, TreeNode<ID, T>>;
  parentId: ID | null;

  constructor(data: T, parentId: ID | null = null) {
    this.data = data;
    this.children = new Map<ID, TreeNode<ID, T>>();
    this.parentId = parentId;
  }
}

export class Tree<ID extends number | string, T extends { id: ID }> {
  root: TreeNode<ID, T> | null;
  nodes: Record<ID, TreeNode<ID, T>>;

  constructor(root?: T) {
    if (!root) {
      this.root = null;
      this.nodes = {} as Record<ID, TreeNode<ID, T>>;
      return;
    }
    this.root = new TreeNode<ID, T>(root);
    this.nodes = { [root.id]: this.root } as Record<ID, TreeNode<ID, T>>;
  }

  delete(elementId: ID) {
    const node = this.nodes[elementId];
    if (!node) return;
    if (node.parentId !== null) {
      this.nodes[node.parentId]?.children.delete(elementId);
    }
    delete this.nodes[elementId];
  }

  getParent(elementId: ID): T | null {
    const node = this.nodes[elementId];
    if (!node?.parentId) return null;
    return this.nodes[node.parentId]?.data ?? null;
  }

  update(element: Partial<T> & { id: ID }) {
    const node = this.nodes[element.id];
    if (!node) return;
    node.data = { ...node.data, ...element };
  }

  insert(element: T, parentElementId?: ID | null): void {
    if (this.nodes[element.id]) return;
    const newNode = new TreeNode<ID, T>(element, parentElementId ?? null);

    this.nodes[element.id] = newNode;

    if (parentElementId === null || parentElementId === undefined)
      this.root = newNode;
    else {
      const parentNode = this.nodes[parentElementId];
      if (parentNode) {
        parentNode.children.set(newNode.data.id, newNode);
      } else {
        throw new Error(`The parent node'${parentElementId}' not exists.`);
      }
    }
  }

  private postOrderTraversal(node: TreeNode<ID, T> | null, result: T[]): void {
    if (node) {
      for (const child of node.children.values()) {
        this.postOrderTraversal(child, result);
      }
      result.push(node.data);
    }
  }

  postOrder(startNodeId?: ID): T[] {
    const result: T[] = [];
    const startNode = startNodeId ? this.nodes[startNodeId] : this.root;
    this.postOrderTraversal(startNode, result);
    return result;
  }

  private findPathHelper(
    node: TreeNode<ID, T> | null,
    targetId: ID,
    path: T[],
  ): boolean {
    if (!node) return false;

    path.push(node.data);

    if (node.data.id === targetId) return true;

    for (const child of node.children.values()) {
      if (this.findPathHelper(child, targetId, path)) return true;
    }

    path.pop();
    return false;
  }

  findPath(targetId: ID): T[] | null {
    const path: T[] = [];
    if (this.findPathHelper(this.root, targetId, path)) {
      return path;
    } else {
      return null;
    }
  }
}
